import React from 'react';

/**
 * HIPAA-compliant ErrorBoundary — catches React rendering crashes and displays
 * a safe fallback UI without exposing any PHI from component props/state.
 *
 * Wraps the entire application so that an unhandled error in any sub-tree
 * shows a generic message instead of a raw error stack (which may contain
 * patient data embedded in props or state).
 */
class ErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, errorId: null };
  }

  static getDerivedStateFromError() {
    // Generate a random error reference for support/correlation
    const errorId = `ERR-${Date.now().toString(36).toUpperCase()}`;
    return { hasError: true, errorId };
  }

  componentDidCatch(error, errorInfo) {
    // Log a SAFE summary — never log component props/state (may contain PHI)
    if (process.env.NODE_ENV === 'development') {
      // In dev, log the error name + component stack (no props/state)
      console.error('[ErrorBoundary] Caught error:', error?.message);
      console.error('[ErrorBoundary] Component stack:', errorInfo?.componentStack);
    }
    // In production you'd send to an error tracking service (Sentry, etc.)
    // with PHI scrubbed. For now we just swallow it.
  }

  handleReload = () => {
    window.location.href = '/';
  };

  render() {
    if (this.state.hasError) {
      // Custom fallback if provided
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div style={styles.container}>
          <div style={styles.card}>
            <div style={styles.icon}>⚠️</div>
            <h1 style={styles.heading}>Something went wrong</h1>
            <p style={styles.body}>
              An unexpected error occurred. No data has been lost.
              Please try reloading the page.
            </p>
            <p style={styles.refId}>
              Reference: {this.state.errorId}
            </p>
            <button style={styles.button} onClick={this.handleReload}>
              Return to Home
            </button>
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

const styles = {
  container: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    minHeight: '100vh',
    backgroundColor: '#f5f5f5',
    fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Helvetica, Arial, sans-serif',
    padding: '20px',
  },
  card: {
    backgroundColor: '#ffffff',
    borderRadius: '12px',
    padding: '48px',
    maxWidth: '480px',
    width: '100%',
    textAlign: 'center',
    boxShadow: '0 4px 24px rgba(0, 0, 0, 0.08)',
  },
  icon: {
    fontSize: '48px',
    marginBottom: '16px',
  },
  heading: {
    fontSize: '24px',
    fontWeight: 700,
    color: '#1a1a1a',
    margin: '0 0 12px 0',
  },
  body: {
    fontSize: '16px',
    color: '#666666',
    lineHeight: 1.6,
    margin: '0 0 8px 0',
  },
  refId: {
    fontSize: '12px',
    color: '#999999',
    margin: '0 0 24px 0',
    fontFamily: 'monospace',
  },
  button: {
    backgroundColor: '#1a1a1a',
    color: '#ffffff',
    border: 'none',
    borderRadius: '8px',
    padding: '12px 32px',
    fontSize: '16px',
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'background-color 0.2s',
  },
};

export default ErrorBoundary;
